import { useState } from 'react'
import { Space, Typography, notification, Row, Col } from 'antd'
import classNames from 'classnames'
import { useAsync } from 'react-use'
import _ from 'lodash'
import { useHistory } from 'react-router-dom'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Table from 'components/Table'
import AllActiveInactive from 'components/AllActiveInactive'

import { useTranslate } from 'hooks/translate'

import { getReportTypeListApi } from 'services/reportType'

import Styled from './styled'

const DEFAULT_PAGE_SIZE = 10

export default function PageReportAttribute () {
  const { t } = useTranslate()
  const history = useHistory()

  const [dataSource, setDataSource] = useState([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    param: null,
    page: 1,
    size: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all') // all, active, inactive

  const _list = async () => {
    if (loading) return

    const params = {
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getReportTypeListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setTotal(res.data?.total)
    setDataSource(res.data?.items)
  }

  useAsync(async () => {
    _list()
  }, [status, filters])

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      className: 'bold',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: t('Reporter'),
      dataIndex: 'reporter',
      key: 'reporter',
      sorter: (a, b) => {
        const reporterCompaniesA = _.filter(a?.companies, { is_reporter: true })
        const reporterCompaniesNameA = _.map(
          reporterCompaniesA,
          (reporterCompany) => reporterCompany?.company_name
        )
        const reporterCompaniesB = _.filter(b?.companies, { is_reporter: true })
        const reporterCompaniesNameB = _.map(
          reporterCompaniesB,
          (reporterCompany) => reporterCompany?.company_name
        )
        const reporterCompaniesNameAA = a?.is_allreporter
          ? 'All'
          : _.join(reporterCompaniesNameA, ', ')
        const reporterCompaniesNameBB = b?.is_allreporter
          ? 'All'
          : _.join(reporterCompaniesNameB, ', ')

        return reporterCompaniesNameAA.localeCompare(reporterCompaniesNameBB)
      },
      render: (text, record, index) => {
        const reporterCompanies = _.filter(record?.companies, { is_reporter: true })
        const reporterDepartments = _.filter(record?.departments, { is_reporter: true })
        const reporterCompaniesName = _.map(
          reporterCompanies,
          (reporterCompany) => reporterCompany?.company_name
        )
        const reporterDepartmentsName = _.map(
          reporterDepartments,
          (reporterDepartment) => reporterDepartment?.department_name
        )
        return (
          <Space direction='vertical' size={0}>
            <Typography.Text>
              Company: {record?.is_allreporter ? 'All' : _.join(reporterCompaniesName, ', ')}
            </Typography.Text>
            <Typography.Text>
              Department: {record?.is_allreporter ? 'All' : _.join(reporterDepartmentsName, ', ')}
            </Typography.Text>
          </Space>
        )
      },
    },
    {
      title: t('Status'),
      dataIndex: ['status', 'label'],
      key: 'status',
      className: 'is-status',
      sorter: (a, b) => a.status.label.localeCompare(b.status.label),
      render: (text, record, index) => {
        return (
          <div
            className={classNames('status-block', {
              'is-active': record.isEnabled,
              'is-inactive': !record.isEnabled,
            })}
          >
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (text, record, index) => {
        return (
          <Space size='small'>
            <Button
              type='primary'
              onClick={() => {
                history.push(`${history.location.pathname}/${record.id}`)
              }}
            >
              {t('Setup')}
            </Button>
          </Space>
        )
      },
    },
  ]

  return (
    <Styled className='page page-report-attribute'>
      <Page
        title={t('Report Type Attribute')}
        searchPlaceholder={t('Search Report Type or description')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Card title={t('Report Type')}>
            <Row justify='end'>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <Table
              loading={loading}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                current: filters.page,
                pageSize: filters.size,
                total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total) => t('Total ${total} items', { total }),
              }}
              onChange={(pagination, filters) => {
                setFilters({
                  ...filters,
                  page: pagination.current,
                  size: pagination.pageSize,
                })
              }}
              scroll={{ x: 800 }}
              sticky
            />
          </Card>
        </Space>
      </Page>
    </Styled>
  )
}
